import React, { FormEvent, useEffect, useState } from "react"
import { Plus, X } from "lucide-react"
import toast from "react-hot-toast"
import LearnerButton from "../../../../component/student/LearnerButton"
import { getLearnerApiErrorMessage } from "../../../../auth/learner/errors"
import "./PersonalNotesTags.style.scss"

interface PersonalNotesTagsProps {
  questionId: string
  note: string
  savedNote: string
  tags: string[]
  savingNote: boolean
  savingTag: boolean
  onNoteChange: (value: string) => void
  onSaveNote: () => Promise<void>
  onClearNote: () => Promise<void>
  onAddTag: (tag: string) => Promise<void>
  onRemoveTag: (tag: string) => Promise<void>
}

const PersonalNotesTags = ({
  questionId,
  note,
  savedNote,
  tags,
  savingNote,
  savingTag,
  onNoteChange,
  onSaveNote,
  onClearNote,
  onAddTag,
  onRemoveTag,
}: PersonalNotesTagsProps) => {
  const [adding, setAdding] = useState(false)
  const [tagDraft, setTagDraft] = useState("")
  const isDirty = note.trim() !== savedNote.trim()
  const canClear = !!savedNote.trim() || !!note.trim()

  // Reset composer when switching questions — not when tags change.
  useEffect(() => {
    setAdding(false)
    setTagDraft("")
  }, [questionId])

  const handleAddTag = async (e?: FormEvent) => {
    e?.preventDefault()
    const value = tagDraft.trim().replace(/^#/, "").trim()
    if (!value || savingTag) return
    try {
      await onAddTag(value)
      setTagDraft("")
      // Keep composer open so another tag can be added immediately.
      setAdding(true)
      toast.success("Tag added")
    } catch (err) {
      toast.error(getLearnerApiErrorMessage(err, "Could not add tag"))
    }
  }

  const handleRemoveTag = async (tag: string) => {
    if (savingTag) return
    try {
      await onRemoveTag(tag)
      toast.success("Tag removed")
    } catch (err) {
      toast.error(getLearnerApiErrorMessage(err, "Could not remove tag"))
    }
  }

  const handleSaveNote = async () => {
    try {
      const clearing = !note.trim()
      if (clearing && !savedNote.trim()) {
        onNoteChange("")
        toast.error("No note found")
        return
      }
      await onSaveNote()
      toast.success(note.trim() ? "Note saved" : "Note cleared")
    } catch (err) {
      toast.error(getLearnerApiErrorMessage(err, "Could not save note"))
    }
  }

  const handleClearNote = async () => {
    try {
      if (!savedNote.trim()) {
        onNoteChange("")
        toast.error("No note found")
        return
      }
      onNoteChange("")
      await onClearNote()
      toast.success("Note cleared")
    } catch (err) {
      toast.error(getLearnerApiErrorMessage(err, "Could not clear note"))
    }
  }

  return (
    <div className="personal-notes-tags">
      <h3>Personal Notes &amp; Tags</h3>

      <div className="personal-notes-tags__tags">
        {tags.map((tag) => (
          <span key={tag} className="personal-notes-tags__chip">
            #{tag}
            <button
              type="button"
              aria-label={`Remove tag ${tag}`}
              disabled={savingTag}
              onClick={() => handleRemoveTag(tag)}
            >
              <X size={12} />
            </button>
          </span>
        ))}

        {adding ? (
          <form className="personal-notes-tags__add-form" onSubmit={handleAddTag}>
            <input
              type="text"
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              placeholder="e.g. review-later"
              maxLength={40}
              autoFocus
              disabled={savingTag}
              aria-label="New tag"
            />
            <button type="submit" disabled={savingTag || !tagDraft.trim()}>
              {savingTag ? "Adding…" : "Add"}
            </button>
            <button
              type="button"
              className="is-cancel"
              disabled={savingTag}
              onClick={() => {
                setAdding(false)
                setTagDraft("")
              }}
            >
              Done
            </button>
          </form>
        ) : (
          <button
            type="button"
            className="personal-notes-tags__add-btn"
            disabled={savingTag || tags.length >= 20}
            onClick={() => setAdding(true)}
          >
            <Plus size={12} /> Add Tag
          </button>
        )}
      </div>

      <textarea
        value={note}
        onChange={(e) => onNoteChange(e.target.value)}
        placeholder="Add private notes for this question..."
      />

      <div className="personal-notes-tags__actions">
        <LearnerButton
          type="button"
          loading={savingNote}
          loadingText="Saving…"
          disabled={!isDirty}
          onClick={handleSaveNote}
        >
          {note.trim() || !savedNote.trim() ? "Save Note" : "Clear Note"}
        </LearnerButton>
        {canClear && (
          <button
            type="button"
            className="personal-notes-tags__clear"
            disabled={savingNote}
            onClick={handleClearNote}
          >
            Clear
          </button>
        )}
        {isDirty && (
          <span className="personal-notes-tags__dirty">Unsaved changes</span>
        )}
      </div>
    </div>
  )
}

export default PersonalNotesTags
